import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { Box, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  CHAT_MENTION_ALL,
  CHAT_ROOM_ALL,
  PRESENCE_STATUS_LABELS,
  buildReplyPreview,
  chatConversationKey,
  type ChatMention,
  type ChatMessage,
  type ChatTarget,
  type PresenceStatus,
} from "@xop/dashboard-shared";
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { useSearchParams } from "react-router-dom";

import { useSessionUser } from "../../auth/AuthProvider.js";
import { PresenceDot, type PresenceState } from "../../components/PresenceDot.js";
import { ProfileCardTrigger } from "../../components/ProfileCardTrigger.js";
import { UserAvatar } from "../../components/UserAvatar.js";
import { setActiveChatConversation } from "../../lib/chat/activeConversation.js";
import {
  useChatOverview,
  useConversation,
  useDeleteMessage,
  useEditMessage,
  useMarkChatRead,
  useSendChatMessage,
  useToggleReaction,
} from "../../lib/chat/hooks.js";
import { useRealtime } from "../../lib/notifications/RealtimeProvider.js";
import { useCallController } from "../../calls/CallProvider.js";
import { useCallsEnabled } from "../../calls/useCallsEnabled.js";
import { ChatComposer } from "./ChatComposer.js";
import { ChatSidebar } from "./ChatSidebar.js";
import { ChatThread } from "./ChatThread.js";

/** Min gap between "typing…" pings while actively typing (server re-broadcasts). */
const TYPING_THROTTLE_MS = 2_500;

/**
 * The Chat surface — a WhatsApp-style two-pane messenger: the conversation
 * list on the left, the open conversation on the right (full-bleed, no
 * SectionHeader, to feel like a real chat app). The active conversation is
 * URL-driven (`/chat` = the room, `/chat?dm=<id>` = a DM), so the global
 * presence rail and external links deep-link straight into a chat. Live
 * messages, typing, presence, and read receipts all come from the realtime
 * provider's shared caches.
 */
export function ChatPage(): JSX.Element {
  const me = useSessionUser();
  const { online, typingByConversation, readByConversation, sendTyping } = useRealtime();
  const overview = useChatOverview();
  const [searchParams, setSearchParams] = useSearchParams();

  const target: ChatTarget = useMemo(() => {
    const dm = searchParams.get("dm");
    if (dm && /^\d+$/.test(dm)) return { kind: "dm", user_id: Number.parseInt(dm, 10) };
    return { kind: "room", room: CHAT_ROOM_ALL };
  }, [searchParams]);
  const selectedKey = chatConversationKey(target);

  // Mobile shows ONE pane at a time (list ↔ conversation), like WhatsApp.
  const [mobileShowConversation, setMobileShowConversation] = useState(() =>
    searchParams.has("dm"),
  );
  // Runtime probe: only show the Call button when the server is configured.
  const callsEnabled = useCallsEnabled();
  const { ring } = useCallController();
  // Drag-and-drop: a file dropped on the conversation pane is handed to the
  // composer to stage (so it shares the attach-button path). `dragging` shows
  // the drop overlay.
  const [dragging, setDragging] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const dragDepth = useRef(0); // enter/leave counter → flicker-free overlay
  const selectTarget = useCallback(
    (t: ChatTarget) => {
      setSearchParams(t.kind === "room" ? {} : { dm: String(t.user_id) });
      setMobileShowConversation(true);
    },
    [setSearchParams],
  );

  // Active reply (WhatsApp-style quote). Holds the original's id (for the send)
  // plus a snapshot of its sender + preview (for the composer banner).
  const [replyTo, setReplyTo] = useState<{
    id: number;
    sender_name: string;
    preview: string;
  } | null>(null);
  const startReply = useCallback(
    (m: ChatMessage) => {
      setReplyTo({
        id: m.id,
        sender_name: m.sender_id === me.id ? "You" : m.sender_name,
        preview: buildReplyPreview({
          body: m.body,
          ...(m.attachment
            ? { attachmentMime: m.attachment.mime, attachmentFilename: m.attachment.filename }
            : {}),
        }),
      });
    },
    [me.id],
  );

  const conversation = useConversation(target);
  const send = useSendChatMessage();
  const toggleReaction = useToggleReaction();
  const editMessage = useEditMessage();
  const deleteMessage = useDeleteMessage();
  const { mutate: markRead } = useMarkChatRead();

  useEffect(() => {
    setActiveChatConversation(selectedKey);
    return () => setActiveChatConversation(null);
  }, [selectedKey]);

  // Drop any in-progress reply when the conversation changes — a quote only
  // makes sense in the conversation it was started from.
  useEffect(() => {
    setReplyTo(null);
  }, [selectedKey]);

  const newestId = conversation.data?.messages.at(-1)?.id ?? 0;
  useEffect(() => {
    markRead(target);
  }, [target, newestId, markRead]);

  // Throttled "typing…" emit.
  const lastTypingAt = useRef(0);
  const onType = useCallback(() => {
    const now = Date.now();
    if (now - lastTypingAt.current > TYPING_THROTTLE_MS) {
      lastTypingAt.current = now;
      sendTyping(target);
    }
  }, [sendTyping, target]);

  const dmContact =
    target.kind === "dm"
      ? overview.data?.contacts.find((c) => c.user_id === target.user_id)
      : undefined;
  const dmState: PresenceState =
    target.kind === "dm"
      ? (online.find((o) => o.user_id === target.user_id)?.status ?? "offline")
      : "offline";
  const typingName = typingByConversation[selectedKey];

  // Who can be @-mentioned here: in the room, everyone; in a DM, only the other
  // party (a mention never reaches a non-participant). `@channel` (the
  // notify-everyone sentinel) leads the room list and is absent in a DM — a
  // "notify everyone" token inside a 1:1 is meaningless (the server drops it
  // there too). It goes FIRST because `filterMentionCandidates` sorts stably
  // within a rank, so the sentinel stays on top of an equal-ranked match.
  const mentionCandidates = useMemo<ChatMention[]>(() => {
    const contacts = overview.data?.contacts ?? [];
    if (target.kind === "dm") {
      return contacts
        .filter((c) => c.user_id === target.user_id)
        .map((c) => ({ user_id: c.user_id, display_name: c.display_name }));
    }
    return [
      CHAT_MENTION_ALL,
      ...contacts.map((c) => ({ user_id: c.user_id, display_name: c.display_name })),
    ];
  }, [overview.data?.contacts, target]);

  const title =
    target.kind === "room"
      ? (overview.data?.room.label ?? "All Staff")
      : (dmContact?.display_name ?? "Direct message");
  const subtitle = headerSubtitle(target, typingName, dmState);

  return (
    <Box
      sx={{
        display: "flex",
        height: "calc(100dvh - 108px)",
        minHeight: 460,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          display: { xs: mobileShowConversation ? "none" : "flex", sm: "flex" },
          width: { xs: "100%", sm: "auto" },
        }}
      >
        <ChatSidebar
          overview={overview.data}
          online={online}
          selectedKey={selectedKey}
          onSelect={selectTarget}
        />
      </Box>

      <Box
        data-testid="chat-conversation-pane"
        sx={{
          position: "relative",
          flex: 1,
          minWidth: 0,
          display: { xs: mobileShowConversation ? "flex" : "none", sm: "flex" },
          flexDirection: "column",
        }}
        onDragEnter={(e) => {
          if (!dragHasFiles(e)) return;
          e.preventDefault();
          dragDepth.current += 1;
          setDragging(true);
        }}
        onDragOver={(e) => {
          if (dragHasFiles(e)) e.preventDefault(); // required to allow the drop
        }}
        onDragLeave={() => {
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setDragging(false);
        }}
        onDrop={(e) => {
          if (!dragHasFiles(e)) return;
          e.preventDefault();
          dragDepth.current = 0;
          setDragging(false);
          const file = e.dataTransfer.files?.[0];
          if (file) setDroppedFile(file);
        }}
      >
        {/* Drag-and-drop overlay — purely visual (pointerEvents: none) so it
            never intercepts the drag events handled on the pane above. */}
        {dragging && (
          <Box
            data-testid="chat-drop-overlay"
            sx={{
              position: "absolute",
              inset: 8,
              zIndex: 5,
              pointerEvents: "none",
              borderRadius: 2,
              border: "2px dashed",
              borderColor: "chat.accent",
              bgcolor: (t) => alpha(t.palette.background.paper, 0.92),
              display: "grid",
              placeItems: "center",
            }}
          >
            <Box sx={{ textAlign: "center", color: "chat.accent" }}>
              <CloudUploadOutlinedIcon sx={{ fontSize: 48 }} />
              <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 1 }}>Drop to send</Typography>
            </Box>
          </Box>
        )}
        {/* Conversation header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 2,
            py: 1,
            bgcolor: "chat.headerBg",
            borderBottom: 1,
            borderColor: "divider",
            minHeight: 60,
          }}
        >
          <IconButton
            onClick={() => setMobileShowConversation(false)}
            aria-label="Back to conversations"
            sx={{ display: { xs: "inline-flex", sm: "none" }, color: "text.secondary", ml: -0.5 }}
          >
            <ArrowBackRoundedIcon />
          </IconButton>
          {target.kind === "dm" && dmContact ? (
            <ProfileCardTrigger userId={dmContact.user_id} name={dmContact.display_name}>
              <UserAvatar name={dmContact.display_name} userId={dmContact.user_id} size={40} presence={dmState} />
            </ProfileCardTrigger>
          ) : (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: "chat.accent",
                color: "#fff",
                display: "grid",
                placeItems: "center",
                fontWeight: 700,
              }}
            >
              ALL
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.2, color: "text.primary" }}>
              {target.kind === "dm" && dmContact ? (
                <ProfileCardTrigger userId={dmContact.user_id} name={dmContact.display_name}>
                  <Box component="span">{title}</Box>
                </ProfileCardTrigger>
              ) : (
                title
              )}
            </Typography>
            <Typography
              data-testid="chat-conversation-subtitle"
              sx={{
                fontSize: 12.5,
                lineHeight: 1.3,
                color: typingName ? "chat.accent" : "text.secondary",
                fontWeight: typingName ? 600 : 400,
              }}
            >
              {subtitle}
            </Typography>
          </Box>
        </Box>

        <ChatThread
          messages={conversation.data?.messages ?? []}
          selfId={me.id}
          loading={conversation.isLoading}
          showSenderNames={target.kind === "room"}
          isDm={target.kind === "dm"}
          recipientOnline={target.kind === "dm" && dmState !== "offline"}
          typingName={typingName ?? null}
          readUpTo={readByConversation[selectedKey] ?? 0}
          onReact={(messageId, emoji) => toggleReaction.mutate({ target, messageId, emoji })}
          onEdit={(messageId, body) => editMessage.mutate({ target, messageId, body })}
          onDelete={(messageId) => deleteMessage.mutate({ target, messageId })}
          onReply={startReply}
        />

        <ChatComposer
          disabled={send.isPending}
          onType={onType}
          placeholder={target.kind === "room" ? "Message All Staff" : `Message ${title}`}
          incomingFile={droppedFile}
          onIncomingFileHandled={() => setDroppedFile(null)}
          mentionCandidates={mentionCandidates}
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          {...(target.kind === "dm" && callsEnabled
            ? { onCall: () => void ring({ user_id: target.user_id, name: dmContact?.display_name ?? title }) }
            : {})}
          onSend={(body, attachmentId, mentionIds) => {
            send.mutate({
              target,
              body,
              ...(attachmentId ? { attachment_id: attachmentId } : {}),
              ...(mentionIds && mentionIds.length > 0 ? { mention_ids: mentionIds } : {}),
              ...(replyTo ? { reply_to: replyTo.id } : {}),
            });
            setReplyTo(null);
          }}
        />

      </Box>
    </Box>
  );
}

/** True when a drag carries OS files (so we only show the drop overlay for
 * file drags, not text/element drags). */
function dragHasFiles(e: DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes("Files");
}

function headerSubtitle(
  target: ChatTarget,
  typingName: string | undefined,
  dmState: PresenceState,
): string {
  if (target.kind === "room") {
    return typingName ? `${typingName} is typing…` : "Everyone on the team";
  }
  if (typingName) return "typing…";
  if (dmState === "active") return "online";
  if (dmState === "offline") return "offline";
  return PRESENCE_STATUS_LABELS[dmState as PresenceStatus].toLowerCase();
}
